import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetMentals, apiCreateMental, apiUpdateMental } from '../services/api';
import { MENTAL, SPORT, UI } from '../constants/colors';
import { EMOTIONAL_SYMPTOMS, NEGATIVE_SYMPTOMS, POSITIVE_SYMPTOMS } from '../constants/emotionalSymptoms';
import { useFormDate } from '../utils/formDate';
import InfoButton from '../components/InfoButton';
import './FormMental.css';

// SVG icons
import { ReactComponent as DropletG } from '../assets/forms/form_emojis/dropletg.svg';
import { ReactComponent as HeartW } from '../assets/forms/form_emojis/heartw.svg';
import { ReactComponent as HeartbeatG } from '../assets/forms/form_emojis/heartbeatg.svg';
import { ReactComponent as CroissantR } from '../assets/forms/form_emojis/croissantr.svg';
import { ReactComponent as CerveauR } from '../assets/forms/form_emojis/cerveaur.svg';
import { ReactComponent as EnergyR } from '../assets/forms/form_emojis/energyr.svg';
import { ReactComponent as HeartR } from '../assets/forms/form_emojis/heartr.svg';
import { ReactComponent as SmileyR } from '../assets/forms/form_emojis/smileyr.svg';
import { ReactComponent as HeartbeatR } from '../assets/forms/form_emojis/heartbeatr.svg';

// PNG emojis - daily mood
import calme from '../assets/forms/form_emojis/calme.png';
import heureuse from '../assets/forms/form_emojis/heureuse.png';
import energetique from '../assets/forms/form_emojis/energetique.png';
import sautes from '../assets/forms/form_emojis/sautes_dhumeur.png';
import irritable from '../assets/forms/form_emojis/irritable.png';
import triste from '../assets/forms/form_emojis/triste.png';
import angoissee from '../assets/forms/form_emojis/angoissee.png';
import deprimee from '../assets/forms/form_emojis/deprimee.png';
import manqueEnergie from '../assets/forms/form_emojis/manque.png';
import autocritique from '../assets/forms/form_emojis/autocritique.png';

// PNG emojis - emotional symptoms
import anxiete from '../assets/forms/form_emojis/deprimee.png';
import stress from '../assets/forms/form_emojis/stress.png';
import irritabilite from '../assets/forms/form_emojis/irritabilite.png';
import fatigueMentale from '../assets/forms/form_emojis/fatigue2.png';
import tristesse from '../assets/forms/form_emojis/triste.png';
import manqueMotivation from '../assets/forms/form_emojis/sautes_dhumeur.png';
import bravo from '../assets/forms/form_emojis/bravo.png';
import energie from '../assets/forms/form_emojis/energie.png';

const HUMEUR_MAP = {
  calme: 'Calme', heureuse: 'Heureuse', energetique: 'Énergique',
  sautes: "Sautes d'humeur", irritable: 'Irritable', triste: 'Triste',
  angoissee: 'Angoissée', deprimee: 'Déprimée',
  'manque-energie': "Manque d'énergie", autocritique: 'Autocritique',
};
const SYMPTOMES_MENTAL_MAP = Object.fromEntries(
  EMOTIONAL_SYMPTOMS.map((s) => [s.id, s.label])
);

// Bilan de la journée, distinct des humeurs : l'humeur nomme un état ressenti
// (Calme, Irritable…), celui-ci juge le déroulé — on peut avoir été irritable
// dans une bonne journée.
//
// Saisi en deux temps : une matinée difficile suivie d'un bon après-midi se résumait à
// « Normale », qui efface les deux.
const RESSENTI_JOUR_MAP = { bonne: 'Bonne', normale: 'Normale', mauvaise: 'Mauvaise' };
const RESSENTI_OPTIONS = [
  { id: 'bonne', emoji: '😊', label: 'Bonne' },
  { id: 'normale', emoji: '😐', label: 'Normale' },
  { id: 'mauvaise', emoji: '😞', label: 'Mauvaise' },
];

// Visuel associé à chaque ressenti (le reste du formulaire utilise des PNG,
// pas des emojis texte).
const SYMPTOME_IMAGES = {
  anxiete, stress, irritabilite, fatigue: fatigueMentale,
  tristesse, 'manque-motivation': manqueMotivation,
  excitee: energetique, 'pleine-forme': bravo, 'pleine-energie': energie,
  sereine: calme, confiante: heureuse,
};

// Reverse maps for pre-population
const HUMEUR_REV = Object.fromEntries(Object.entries(HUMEUR_MAP).map(([k, v]) => [v, k]));
const SYMPTOMES_MENTAL_REV = Object.fromEntries(Object.entries(SYMPTOMES_MENTAL_MAP).map(([k, v]) => [v, k]));
const RESSENTI_JOUR_REV = Object.fromEntries(Object.entries(RESSENTI_JOUR_MAP).map(([k, v]) => [v, k]));

const FormMental = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();
  // Jour édité : `?date=` s'il est fourni (rattrapage depuis le calendrier), sinon
  // aujourd'hui. Ce formulaire écrivait « aujourd'hui » en dur, quel que soit le jour
  // sélectionné — la saisie rétroactive était donc impossible côté mental.
  const { selectedDate, isBackfill, label: dateLabel, withDate } = useFormDate();
  const [error, setError] = useState('');
  const [existingId, setExistingId] = useState(null);

  // États du formulaire
  const [jourReposMental, setJourReposMental] = useState(false);
  const [concentration, setConcentration] = useState(1);
  const [productivite, setProductivite] = useState(1);
  const [estime, setEstime] = useState(1);
  const [humeurs, setHumeurs] = useState([]);
  const [humeurPrincipale, setHumeurPrincipale] = useState(null);
  const [symptomes, setSymptomes] = useState([]);
  const [ressentiMatin, setRessentiMatin] = useState(null);
  const [ressentiAprem, setRessentiAprem] = useState(null);
  const [note, setNote] = useState('');

  // Pré-remplissage à partir du relevé du jour édité, s'il existe déjà
  useEffect(() => {
    if (!user?.id) return;
    apiGetMentals(user.id, getToken()).then((entries) => {
      const entry = entries.find((e) => e.date === selectedDate);
      if (!entry) return;
      setExistingId(entry.id);
      setJourReposMental(entry.is_rest_day ?? false);
      setConcentration(entry.concentration ?? 1);
      setProductivite(entry.productivity ?? 1);
      setEstime(entry.self_esteem ?? 1);
      const humeurKey = entry.mood ? (HUMEUR_REV[entry.mood] ?? null) : null;
      setHumeurs(humeurKey ? [humeurKey] : []);
      setHumeurPrincipale(null);
      setSymptomes(
        (entry.emotional_symptoms ?? []).map((s) => SYMPTOMES_MENTAL_REV[s]).filter(Boolean)
      );
      // Journée saisie avant le découpage : son ressenti unique repeuple les deux
      // moments, sinon rouvrir un ancien relevé le montrerait vide.
      const toKey = (v) => (v ? (RESSENTI_JOUR_REV[v] ?? null) : null);
      const legacy = toKey(entry.day_feeling);
      setRessentiMatin(toKey(entry.day_feeling_morning) ?? legacy);
      setRessentiAprem(toKey(entry.day_feeling_afternoon) ?? legacy);
      setNote(entry.notes ?? '');
    }).catch(() => {});
  }, [user?.id, getToken, selectedDate]);

  const toggleHumeur = (id) => {
    setHumeurs(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (next.length <= 1) setHumeurPrincipale(null);
      return next;
    });
  };

  const toggleSymptome = (s) => {
    setSymptomes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const handleSubmit = async () => {
    setError('');
    const payload = {
      date: selectedDate,
      is_rest_day: jourReposMental,
      concentration: parseInt(concentration, 10),
      productivity: parseInt(productivite, 10),
      self_esteem: parseInt(estime, 10),
      mood: humeurPrincipale ? HUMEUR_MAP[humeurPrincipale] : (humeurs.length === 1 ? HUMEUR_MAP[humeurs[0]] : null),
      emotional_symptoms: symptomes.map((s) => SYMPTOMES_MENTAL_MAP[s]).filter(Boolean),
      day_feeling_morning: ressentiMatin ? RESSENTI_JOUR_MAP[ressentiMatin] : null,
      day_feeling_afternoon: ressentiAprem ? RESSENTI_JOUR_MAP[ressentiAprem] : null,
      // Le champ d'avant le découpage n'est plus écrit : le PUT remplaçant tout le blob,
      // l'omettre le remettrait à null de toute façon — autant le dire explicitement.
      day_feeling: null,
      notes: note || null,
    };
    try {
      let mentalResponse;
      if (existingId) {
        const { date: _, ...payloadWithoutDate } = payload;
        mentalResponse = await apiUpdateMental(existingId, payloadWithoutDate, getToken());
      } else {
        mentalResponse = await apiCreateMental(payload, getToken());
      }
      // Ouvert depuis « Ajouter mon mental » : seul ce volet était à saisir.
      if (location.state?.solo) { navigate('/accueil'); return; }
      navigate(withDate('/formulaire/sport'), { state: { cycleData: location.state?.cycleData, mentalData: mentalResponse } });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="form-mental">
      {/* Header */}
      <div className="form-mental__header">
        <div className="form-mental__header-top">
          <div className="form-mental__header-left">
            <div className="form-mental__header-logo">
              <HeartW className="form-mental__header-icon" />
            </div>
            <div>
              <h1 className="form-mental__header-title">Émotions & état mental</h1>
              {/* En rattrapage, le jour édité doit être visible : sans ça, rien ne
                  distingue à l'écran la saisie du jour de celle d'un jour passé. */}
              <p className="form-mental__header-sub">
                {isBackfill ? `Saisie pour ${dateLabel}` : "Comment te sens-tu aujourd'hui ?"}
              </p>
            </div>
          </div>
          <button className="form-mental__close" onClick={() => navigate('/accueil')} aria-label="Fermer">✕</button>
        </div>

        {/* Onglets catégories */}
        <div className="form-mental__tabs-wrapper">
          <div className="form-mental__tabs">
            <button className="form-mental__tab" onClick={() => navigate(withDate('/formulaire/cycle'))}>
              <DropletG className="form-mental__tab-icon" /> Cycle
            </button>
            <button className="form-mental__tab form-mental__tab--active">
              <HeartW className="form-mental__tab-icon" /> Mental
            </button>
            <button className="form-mental__tab" onClick={() => navigate(withDate('/formulaire/sport'))}>
              <HeartbeatG className="form-mental__tab-icon" /> Sport
            </button>
          </div>
        </div>
      </div>

      {/* ===== Jour de repos mental ===== */}
      <div className="form-mental__section form-mental__section--bordered form-mental__section--rest">
        <div className="form-mental__section-header">
          <CroissantR className="form-mental__section-icon croissant" />
          <div>
            <h3 className="form-mental__section-title">Jour de repos mental</h3>
            <p className="form-mental__section-sub">Pas de suivi mental</p>
          </div>
        </div>
        <label className="form-mental__switch">
          <input type="checkbox" checked={jourReposMental} onChange={() => setJourReposMental(!jourReposMental)} />
          <span className="form-mental__switch-slider" />
        </label>
      </div>

      {/* ===== Concentration ===== */}
      <div className={`form-mental__section form-mental__section--col form-mental__section--bordered${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <CerveauR className="form-mental__section-icon" />
          <div className="form-mental__section-title-row">
            <h3 className="form-mental__section-title">Concentration</h3>
            <span className="form-mental__slider-value">{concentration}/10</span>
          </div>
        </div>
        <p className="form-mental__section-sub">À quel point arrives-tu à te concentrer ?</p>
        <input
          type="range"
          min="1"
          max="10"
          value={concentration}
          onChange={(e) => setConcentration(e.target.value)}
          className="form-mental__slider"
          style={{
            background: `linear-gradient(to right, ${MENTAL.sliderActive} 0%, ${MENTAL.sliderActive} ${(concentration - 1) / 9 * 100}%, ${SPORT.jourRepos} ${(concentration - 1) / 9 * 100}%, ${SPORT.jourRepos} 100%)`
          }}
        />
        <div className="form-mental__slider-labels">
          <span>Très bas</span>
          <span>Très élevé</span>
        </div>
      </div>

      {/* ===== Productivité ===== */}
      <div className={`form-mental__section form-mental__section--col form-mental__section--bordered${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <EnergyR className="form-mental__section-icon" />
          <div className="form-mental__section-title-row">
            <h3 className="form-mental__section-title">Productivité</h3>
            <span className="form-mental__slider-value">{productivite}/10</span>
          </div>
        </div>
        <p className="form-mental__section-sub">Comment évalues-tu ta productivité ?</p>
        <input
          type="range"
          min="1"
          max="10"
          value={productivite}
          onChange={(e) => setProductivite(e.target.value)}
          className="form-mental__slider"
          style={{
            background: `linear-gradient(to right, ${MENTAL.sliderActive} 0%, ${MENTAL.sliderActive} ${(productivite - 1) / 9 * 100}%, ${SPORT.jourRepos} ${(productivite - 1) / 9 * 100}%, ${SPORT.jourRepos} 100%)`
          }}
        />
        <div className="form-mental__slider-labels">
          <span>Très bas</span>
          <span>Très élevé</span>
        </div>
      </div>

      {/* ===== Estime de soi ===== */}
      <div className={`form-mental__section form-mental__section--col form-mental__section--bordered${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <HeartR className="form-mental__section-icon" />
          <div className="form-mental__section-title-row">
            <h3 className="form-mental__section-title">Estime de soi</h3>
            <span className="form-mental__slider-value">{estime}/10</span>
          </div>
        </div>
        <p className="form-mental__section-sub">Comment te sens-tu dans ta peau ?</p>
        <input
          type="range"
          min="1"
          max="10"
          value={estime}
          onChange={(e) => setEstime(e.target.value)}
          className="form-mental__slider"
          style={{
            background: `linear-gradient(to right, ${MENTAL.sliderActive} 0%, ${MENTAL.sliderActive} ${(estime - 1) / 9 * 100}%, ${SPORT.jourRepos} ${(estime - 1) / 9 * 100}%, ${SPORT.jourRepos} 100%)`
          }}
        />
        <div className="form-mental__slider-labels">
          <span>Très bas</span>
          <span>Très élevé</span>
        </div>
      </div>

      {/* ===== Humeur du jour ===== */}
      {(() => {
        const ALL_MOODS = [
          { id: 'calme', img: calme, label: 'Calme' },
          { id: 'heureuse', img: heureuse, label: 'Heureuse' },
          { id: 'energetique', img: energetique, label: 'Énergique' },
          { id: 'sautes', img: sautes, label: "Sautes d'humeur" },
          { id: 'irritable', img: irritable, label: 'Irritable' },
          { id: 'triste', img: triste, label: 'Triste' },
          { id: 'angoissee', img: angoissee, label: 'Angoissée' },
          { id: 'deprimee', img: deprimee, label: 'Déprimée' },
          { id: 'manque-energie', img: manqueEnergie, label: "Manque d'énergie" },
          { id: 'autocritique', img: autocritique, label: 'Autocritique' },
        ];
        return (
          <div className={`form-mental__section form-mental__section--col${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
            <div className="form-mental__section-header">
              <SmileyR className="form-mental__section-icon" />
              <div>
                <h3 className="form-mental__section-title">Humeur du jour</h3>
                <p className="form-mental__section-sub">Sélectionne une ou plusieurs émotions</p>
              </div>
            </div>
            <div className="form-mental__options form-mental__options--5col">
              {ALL_MOODS.map((o) => (
                <button
                  key={o.id}
                  className={`form-mental__option${humeurs.includes(o.id) ? ' form-mental__option--selected' : ''}`}
                  onClick={() => toggleHumeur(o.id)}
                >
                  <img src={o.img} alt={o.label} className="form-mental__option-img" />
                  <span className="form-mental__option-label">{o.label}</span>
                </button>
              ))}
            </div>
            {humeurs.length > 1 && (
              <div className="form-mental__primary-mood">
                <p style={{ fontSize: '0.85rem', color: UI.textMuted, margin: '0.75rem 0 0.5rem' }}>Laquelle représente le mieux ta journée ?</p>
                <div className="form-mental__options form-mental__options--5col">
                  {humeurs.map(id => {
                    const o = ALL_MOODS.find(m => m.id === id);
                    return (
                      <button key={id}
                        className={`form-mental__option${humeurPrincipale === id ? ' form-mental__option--selected' : ''}`}
                        onClick={() => setHumeurPrincipale(id)}>
                        <img src={o.img} alt={o.label} className="form-mental__option-img" />
                        <span className="form-mental__option-label">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ===== Symptômes émotionnels ===== */}
      <div className={`form-mental__section form-mental__section--col${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <HeartbeatR className="form-mental__section-icon" />
          <div>
            <h3 className="form-mental__section-title">Ressentis émotionnels<InfoButton infoKey="formSymptomesEmotionnels" /></h3>
            <p className="form-mental__section-sub">Qu'est-ce que tu ressens ? Le positif compte aussi.</p>
          </div>
        </div>

        <p className="form-mental__group-label">Ce qui pèse</p>
        <div className="form-mental__options form-mental__options--3col">
          {NEGATIVE_SYMPTOMS.map((o) => (
            <button
              key={o.id}
              className={`form-mental__option${symptomes.includes(o.id) ? ' form-mental__option--selected' : ''}`}
              onClick={() => toggleSymptome(o.id)}
            >
              <img src={SYMPTOME_IMAGES[o.id]} alt={o.label} className="form-mental__option-img" />
              <span className="form-mental__option-label">{o.label}</span>
            </button>
          ))}
        </div>

        <p className="form-mental__group-label">Ce qui porte</p>
        <div className="form-mental__options form-mental__options--3col">
          {POSITIVE_SYMPTOMS.map((o) => (
            <button
              key={o.id}
              className={`form-mental__option${symptomes.includes(o.id) ? ' form-mental__option--selected' : ''}`}
              onClick={() => toggleSymptome(o.id)}
            >
              <img src={SYMPTOME_IMAGES[o.id]} alt={o.label} className="form-mental__option-img" />
              <span className="form-mental__option-label">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Ressenti de la journée ===== */}
      {/* Placé juste avant les notes : c'est un bilan, il se remplit une fois le
          reste renseigné. Distinct des humeurs — on peut avoir été irritable dans
          une journée qui s'est bien passée. */}
      <div className={`form-mental__section form-mental__section--col${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <SmileyR className="form-mental__section-icon" />
          <div>
            <h3 className="form-mental__section-title">Ressenti de la journée</h3>
            <p className="form-mental__section-sub">Comment s'est passé chaque moment ?</p>
          </div>
        </div>
        {/* Deux temps plutôt qu'un : une matinée difficile suivie d'un bon après-midi
            se résumait à « Normale », qui n'en dit ni l'une ni l'autre. */}
        {[
          { label: 'Matin', value: ressentiMatin, set: setRessentiMatin },
          { label: 'Après-midi', value: ressentiAprem, set: setRessentiAprem },
        ].map(({ label, value, set }) => (
          <div key={label} className="form-mental__moment">
            <p className="form-mental__moment-label">{label}</p>
            <div className="form-mental__options form-mental__options--3col">
              {RESSENTI_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  className={`form-mental__option${value === o.id ? ' form-mental__option--selected' : ''}`}
                  aria-pressed={value === o.id}
                  aria-label={`${label} : ${o.label}`}
                  onClick={() => set(value === o.id ? null : o.id)}
                >
                  <span className="form-mental__option-emoji">{o.emoji}</span>
                  <span className="form-mental__option-label">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ===== Note personnelle ===== */}
      <div className={`form-mental__section form-mental__section--col form-mental__section--note${jourReposMental ? ' form-mental__section--hidden' : ''}`}>
        <div className="form-mental__section-header">
          <HeartR className="form-mental__section-icon" />
          <h3 className="form-mental__section-title">Note personnelle (optionnel)</h3>
        </div>
        <p className="form-mental__note-sub">Partage tes pensées du jour...</p>
        <textarea
          className="form-mental__textarea form-mental__textarea--note"
          placeholder="Aujourd'hui je me suis sentie..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
        />
      </div>

      {/* ===== Footer Enregistrer ===== */}
      <div className="form-mental__footer">
        {error && <p style={{ color: 'var(--danger)', textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</p>}
        <button className="form-mental__submit" onClick={handleSubmit}>
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default FormMental;
